import { digObject } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntitiesState } from '@redux/entities'

import type { UseArtworkPayload } from '@hooks/useArtwork'

import type {
  ArtworkModel,
  ArtworkPageDataChange,
  MappedArtworkPageDataChange,
} from '@models/types'

const defaultRequestOptions = {
  artwork: {
    include_artwork_campaign: true,
    include_artwork_pages: true,
  },
  artworkPage: {
    include_artwork_page_attachments: true,
    include_artwork_page_data: true,
    include_artwork_page_preview: true,
  },
}

type DefaultState = {
  updating: boolean,
}

const defaultState: DefaultState = {
  updating: false,
}

type SetState = (state: Partial<DefaultState>) => void

type GetArtworkDataChangesParams = {
  artwork: ArtworkModel,
  artworkPages: EntitiesState['artworkPages'],
  templatePages: EntitiesState['templatePages'],
}

type ArtworkDataChangesPage = {
  changes: ArtworkPageDataChange[],
  id: number | string,
  templatePageId: number,
  title: string,
}

type ArtworkDataChanges = {
  count: number,
  pages: ArtworkDataChangesPage[],
}

const getArtworkDataChanges = (params: GetArtworkDataChangesParams) => {
  const {
    artwork,
    artworkPages,
    templatePages,
  } = params

  const changes: ArtworkDataChanges = {
    count: 0,
    pages: [],
  }

  const pages = Object.values(artworkPages)
  pages.forEach((page) => {
    if (page.artwork_id === artwork.id){
      const { title } = templatePages[page.template_page_id] || {}
      const pageChanges = page.data_changes

      if (pageChanges) changes.count += pageChanges.length

      changes.pages.push({
        changes: pageChanges || [],
        id: page.id,
        templatePageId: page.template_page_id,
        title: title || `Artwork Page #${page.id}`,
      })
    }
  })

  return changes
}

const mapChangesForSync = (pageChanges: ArtworkDataChangesPage[]) => {
  const mappedChanges: MappedArtworkPageDataChange[] = []

  pageChanges.forEach((page) => {
    if (page.changes){
      page.changes.forEach((c) => {
        mappedChanges.push({
          artwork_page_id: page.id,
          artwork_data_key: c.artwork_data_key,
          id: c.id,
          patch: c.patch,
        })
      })
    }
  })

  return mappedChanges
}

type SyncDataChangesParams = {
  mappedChanges: MappedArtworkPageDataChange[],
  patchArtworkData: UseArtworkPayload['callbacks']['patchArtworkData'],
  setState: SetState,
}

const syncDataChanges = (params: SyncDataChangesParams) => {
  const {
    mappedChanges,
    patchArtworkData,
    setState,
  } = params

  setState({ updating: true })

  patchArtworkData(mappedChanges, { ...defaultRequestOptions.artwork, ...defaultRequestOptions.artworkPage }).then(
    () => {
      setState({ updating: false })
    },
  )
}

type UseArtworkDataChangesOptions = {
  artwork: ArtworkModel,
  callbacks: {
    patchArtworkData: UseArtworkPayload['callbacks']['patchArtworkData'],
  },
  performHttpRequests?: boolean,
}

function useArtworkDataChanges(options: UseArtworkDataChangesOptions) {
  const {
    artwork,
    callbacks: {
      patchArtworkData,
    },
    performHttpRequests = false,
  } = options

  const { campaign_id, id } = artwork

  const [state, setState] = useSetState(defaultState)
  const { updating } = state

  const entities = useSelector(reduxState => reduxState.entities)
  const { artworkPages, campaigns, templatePages } = entities

  const campaign = campaigns[campaign_id] || {}
  const updatedDate = digObject(campaign, 'dates.updated.date_time')

  // Load Changes
  useReduxAction(
    'artworks',
    'loadArtwork',
    {},
    [id],
    {
      dispatchAction: action => action(
        artwork.id,
        {
          ...defaultRequestOptions.artwork,
          ...defaultRequestOptions.artworkPage,
        },
      ),
      shouldPerformFn: (entityReducer) => {
        const { errors } = entityReducer
        return performHttpRequests && !!id && !errors?.length
      },
    },
  )

  const artworkDataChanges = getArtworkDataChanges({
    artwork,
    artworkPages,
    templatePages,
  })

  const artworkMappedChanges = mapChangesForSync(artworkDataChanges.pages)

  return {
    artworkDataChanges,
    artworkMappedChanges,
    callbacks: {
      syncDataChanges: (suppliedChanges: MappedArtworkPageDataChange[]) => (
        syncDataChanges({
          mappedChanges: suppliedChanges,
          patchArtworkData,
          setState,
        })
      ),
    },
    lastUpdated: updatedDate,
    updating,
  }
}

export type UseArtworkDataChangesPayload = ReturnType<typeof useArtworkDataChanges>

export default useArtworkDataChanges
